import React, { useEffect, useState } from 'react';
import { api } from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import { usePublications, PublicationStatus, PublicationType } from '../../../hooks/usePublications';
import { useSubscriptionStats } from '../../../hooks/useSubscription';
import { useTenant } from '../../../contexts/TenantContext';
import './PublicationsDashboard.css';

interface PublicationsDashboardProps {
  tenantId?: string;
}

const PublicationsDashboard: React.FC<PublicationsDashboardProps> = ({ tenantId: propTenantId }) => {
  const { tenant } = useTenant();
  const tenantId = propTenantId || tenant?.id || '';
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<PublicationStatus | 'ALL'>('ALL');
  const [typeFilter, setTypeFilter] = useState<PublicationType | 'ALL'>('ALL');

  const { publications, loading, error, publish, archive, remove } = usePublications(
    {
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
      type: typeFilter !== 'ALL' ? typeFilter : undefined,
      limit: 20,
    },
    tenantId
  );

  const { stats: subscriberStats } = useSubscriptionStats(tenantId);

  // Abonnés par email (lettre d'actualités, sans compte) — distincts des
  // abonnements liés à un compte (push, jamais configuré).
  const [emailSubscribers, setEmailSubscribers] = useState<number | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [emailedOverride, setEmailedOverride] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!tenantId) return;
    api
      .get('/newsletter/subscribers', { params: { status: 'CONFIRMED' }, headers: { 'X-Tenant-ID': tenantId } })
      .then((r) => setEmailSubscribers(r?.counts?.CONFIRMED ?? 0))
      .catch(() => setEmailSubscribers(null));
  }, [tenantId]);

  const handleSendEmail = async (pub: { id: string; title: string; emailed_at?: string | null }) => {
    const already = emailedOverride[pub.id] || pub.emailed_at;
    const count = emailSubscribers ?? 0;
    if (count === 0) {
      alert('Aucun abonné confirmé pour le moment : rien à envoyer.');
      return;
    }
    const question = already
      ? `« ${pub.title} » a DÉJÀ été envoyée le ${new Date(already).toLocaleDateString('fr-BE')}. La renvoyer quand même à ${count} abonné(s) ?`
      : `Envoyer « ${pub.title} » par email à ${count} abonné(s) confirmé(s) ?`;
    if (!confirm(question)) return;
    setSendingId(pub.id);
    try {
      const r = await api.post(`/newsletter/send/${pub.id}`, {}, {
        params: already ? { force: 'true' } : {},
        headers: { 'X-Tenant-ID': tenantId },
      });
      setEmailedOverride((prev) => ({ ...prev, [pub.id]: new Date().toISOString() }));
      alert(`Envoyé à ${r.sent} abonné(s).`);
    } catch (error: any) {
      alert('Erreur : ' + (error.response?.data?.message || error.message));
    } finally {
      setSendingId(null);
    }
  };

  const handlePublish = async (id: string) => {
    if (!confirm('Publier cette publication ? Tous vos abonnés seront notifiés.')) return;

    try {
      await publish(id);
      alert('Publication publiée et abonnés notifiés !');
    } catch (error: any) {
      alert('Erreur : ' + error.message);
    }
  };

  const handleArchive = async (id: string) => {
    try {
      await archive(id);
    } catch (error: any) {
      alert('Erreur : ' + error.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer définitivement cette publication ?')) return;

    try {
      await remove(id);
    } catch (error: any) {
      alert('Erreur : ' + error.message);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; color: string }> = {
      DRAFT: { label: 'Brouillon', color: '#6b7280' },
      SCHEDULED: { label: 'Programmée', color: '#3b82f6' },
      PUBLISHED: { label: 'Publiée', color: '#10b981' },
      EXPIRED: { label: 'Expirée', color: '#f59e0b' },
      ARCHIVED: { label: 'Archivée', color: '#9ca3af' },
    };

    const badge = badges[status] || badges.DRAFT;

    return (
      <span className="status-badge" style={{ background: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      ARRIVAGE: '🍅',
      PROMOTION: '🎉',
      ANTI_GASPI: '🌱',
      EVENEMENT: '🎪',
      INFO: 'ℹ️',
    };
    return icons[type] || '📢';
  };

  return (
    <div className="publications-dashboard">
      {/* Header avec KPIs */}
      <div className="dashboard-header">
        <div>
          <h1>Publications</h1>
          <p className="subtitle">Gérez vos communications avec vos clients</p>
        </div>
        <button
          onClick={() => navigate('/publications/create')}
          className="btn-create"
        >
          ➕ Nouvelle publication
        </button>
      </div>

      {/* KPIs */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon">📢</div>
          <div className="kpi-value">{publications?.length || 0}</div>
          <div className="kpi-label">Publications actives</div>
        </div>

        <div className="kpi-card" onClick={() => navigate('/publications/abonnes')} style={{ cursor: 'pointer' }} title="Voir les abonnés par email">
          <div className="kpi-icon">✉️</div>
          <div className="kpi-value">{emailSubscribers ?? '—'}</div>
          <div className="kpi-label">Abonnés email</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon">👥</div>
          <div className="kpi-value">{subscriberStats?.total || 0}</div>
          <div className="kpi-label">Abonnés (compte)</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon">👁️</div>
          <div className="kpi-value">
            {publications?.reduce((sum, p) => sum + p.views_count, 0) || 0}
          </div>
          <div className="kpi-label">Vues totales</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon">📊</div>
          <div className="kpi-value">
            {publications && publications.length > 0
              ? Math.round(
                  (publications.reduce((sum, p) => sum + p.clicks_count, 0) /
                    publications.reduce((sum, p) => sum + p.views_count, 0)) *
                    100
                )
              : 0}
            %
          </div>
          <div className="kpi-label">Taux d'engagement</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Statut :</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
            <option value="ALL">Tous</option>
            <option value="DRAFT">Brouillons</option>
            <option value="SCHEDULED">Programmées</option>
            <option value="PUBLISHED">Publiées</option>
            <option value="EXPIRED">Expirées</option>
            <option value="ARCHIVED">Archivées</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type :</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)}>
            <option value="ALL">Tous</option>
            <option value="ARRIVAGE">🍅 Arrivages</option>
            <option value="PROMOTION">🎉 Promotions</option>
            <option value="ANTI_GASPI">🌱 Anti-gaspi</option>
            <option value="EVENEMENT">🎪 Événements</option>
            <option value="INFO">ℹ️ Informations</option>
          </select>
        </div>
      </div>

      {/* Liste des publications */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>⚠️ {error}</p>
        </div>
      ) : !publications || publications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>Aucune publication</h3>
          <p>Créez votre première publication pour communiquer avec vos clients</p>
          <button
            onClick={() => navigate('/publications/create')}
            className="btn-create"
          >
            ➕ Créer une publication
          </button>
        </div>
      ) : (
        <div className="publications-table">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Titre</th>
                <th>Statut</th>
                <th>Vues</th>
                <th>Engagement</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {publications?.map((pub) => (
                <tr key={pub.id}>
                  <td>
                    <span className="type-icon">{getTypeIcon(pub.type)}</span>
                  </td>
                  <td>
                    <div
                      className="publication-title-cell"
                      onClick={() => navigate(`/publications/${pub.id}`)}
                    >
                      {pub.title}
                      {pub.images && pub.images.length > 0 && (
                        <span className="media-badge">📷 {pub.images.length}</span>
                      )}
                      {pub.products && pub.products.length > 0 && (
                        <span className="media-badge">🛒 {pub.products.length}</span>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(pub.status)}</td>
                  <td>
                    <span className="stat-value">{pub.views_count}</span>
                  </td>
                  <td>
                    <span className="stat-value">
                      {pub.views_count > 0
                        ? Math.round((pub.clicks_count / pub.views_count) * 100)
                        : 0}
                      %
                    </span>
                  </td>
                  <td className="date-cell">
                    {new Date(pub.published_at || pub.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td>
                    <div className="actions-cell">
                      {pub.status === 'DRAFT' && (
                        <button
                          onClick={() => handlePublish(pub.id)}
                          className="btn-action btn-publish"
                          title="Publier"
                        >
                          📢
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/publications/${pub.id}/edit`)}
                        className="btn-action btn-edit"
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => navigate(`/publications/${pub.id}/analytics`)}
                        className="btn-action btn-analytics"
                        title="Statistiques"
                      >
                        📊
                      </button>
                      {pub.status === 'PUBLISHED' && (
                        <button
                          onClick={() => handleSendEmail(pub)}
                          className="btn-action btn-email"
                          disabled={sendingId === pub.id}
                          title={
                            emailedOverride[pub.id] || pub.emailed_at
                              ? `Envoyée par email le ${new Date(emailedOverride[pub.id] || pub.emailed_at!).toLocaleDateString('fr-BE')}`
                              : 'Envoyer par email aux abonnés'
                          }
                          style={{ opacity: emailedOverride[pub.id] || pub.emailed_at ? 0.45 : 1 }}
                        >
                          {sendingId === pub.id ? '⏳' : '✉️'}
                        </button>
                      )}
                      {pub.status === 'PUBLISHED' && (
                        <button
                          onClick={() => handleArchive(pub.id)}
                          className="btn-action btn-archive"
                          title="Archiver"
                        >
                          📦
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(pub.id)}
                        className="btn-action btn-delete"
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PublicationsDashboard;
